// src/components/CommunitySection.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";

const CommunitySection = () => {
  const { t } = useLanguage();

  return (
    <section id="comunidad" className="w-full max-w-7xl mx-auto px-6 py-14">
      <div
        className="
          relative
          overflow-hidden
          rounded-[2.5rem]
          border-2
          border-purple-300/80
          dark:border-purple-500/40
          bg-gradient-to-br
          from-purple-50
          via-indigo-50/50
          to-white
          dark:from-[#12072B]
          dark:via-[#1B0B3B]
          dark:to-[#0A0518]
          p-8
          md:p-14
          shadow-lg
          dark:shadow-[0_0_50px_rgba(147,51,234,0.25)]
          text-slate-900
          dark:text-white
          transition-colors
          duration-300
        "
      >
        {/* LUCES AMBIENTALES */}
        <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 bg-purple-500/10 dark:bg-purple-500/20 rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 w-80 h-80 bg-cyan-500/10 dark:bg-cyan-500/15 rounded-full blur-[100px]" />

        <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
          {/* TEXTO */}
          <div className="lg:col-span-8 space-y-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-600 text-white font-black text-xs uppercase tracking-wider shadow-md">
              <span>🌐</span>
              <span>{t("community.badge")}</span>
            </span>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight text-slate-900 dark:text-white">
              {t("community.title")}
            </h2>

            <p className="text-slate-600 dark:text-purple-100 text-base md:text-lg leading-relaxed max-w-2xl font-normal">
              {t("community.subtitle")}
            </p>

            {/* BOTONES */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Link
                to="/cursos"
                className="
                  px-7
                  py-3.5
                  rounded-2xl
                  bg-gradient-to-r
                  from-amber-400
                  to-yellow-400
                  hover:from-amber-300
                  hover:to-yellow-300
                  text-slate-950
                  font-black
                  text-xs
                  tracking-wider
                  uppercase
                  shadow-lg
                  transition-all
                  duration-300
                  flex
                  items-center
                  gap-2
                "
              >
                <span>{t("community.btnExploreCourses")}</span>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>

              <a
                href="https://www.youtube.com/@henkivzla"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  px-6
                  py-3.5
                  rounded-2xl
                  border-2
                  border-slate-300
                  dark:border-white/20
                  bg-white
                  hover:bg-slate-100
                  dark:bg-white/10
                  dark:hover:bg-white/20
                  text-slate-900
                  dark:text-white
                  font-bold
                  text-xs
                  transition-all
                  duration-300
                  flex
                  items-center
                  gap-2.5
                  shadow-sm
                  backdrop-blur-md
                "
              >
                <img src="/assets/icons/youtube.svg" alt="YouTube" className="w-4 h-4" />
                <span>{t("community.btnSubscribe")}</span>
              </a>

              <a
                href="https://www.instagram.com/henkivzla/"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  px-6
                  py-3.5
                  rounded-2xl
                  border-2
                  border-slate-300
                  dark:border-white/20
                  bg-white
                  hover:bg-slate-100
                  dark:bg-white/10
                  dark:hover:bg-white/20
                  text-slate-900
                  dark:text-white
                  font-bold
                  text-xs
                  transition-all
                  duration-300
                  flex
                  items-center
                  gap-2.5
                  shadow-sm
                  backdrop-blur-md
                "
              >
                <img src="/assets/icons/instagram.svg" alt="Instagram" className="w-4 h-4" />
                <span>{t("community.btnInstagram")}</span>
              </a>
            </div>
          </div>

          {/* TARJETA VISUAL COMPACTA */}
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-full max-w-sm rounded-3xl border-2 border-purple-200 dark:border-white/15 bg-white dark:bg-white/5 p-6 backdrop-blur-xl space-y-4 text-center shadow-md dark:shadow-none">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-purple-500 to-indigo-500 text-white flex items-center justify-center text-3xl mx-auto shadow-xl">
                🎓
              </div>

              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">
                  Henki Academy
                </h3>
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-200 mt-1">
                  {t("community.cardSubtitle")}
                </p>
              </div>

              <div className="space-y-2 text-xs text-left bg-purple-50/80 dark:bg-black/30 p-3.5 rounded-2xl border border-purple-200/80 dark:border-white/10">
                <p className="text-slate-800 dark:text-purple-200 flex items-center gap-2 font-medium">
                  <span className="text-amber-500 font-bold">✓</span> {t("community.feature1")}
                </p>
                <p className="text-slate-800 dark:text-purple-200 flex items-center gap-2 font-medium">
                  <span className="text-amber-500 font-bold">✓</span> {t("community.feature2")}
                </p>
                <p className="text-slate-800 dark:text-purple-200 flex items-center gap-2 font-medium">
                  <span className="text-amber-500 font-bold">✓</span> {t("community.feature3")}
                </p>
              </div>

              <Link
                to="/cursos"
                className="block w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs transition shadow-sm"
              >
                {t("community.cardCta")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunitySection;
